import React, { useEffect, useState, useMemo } from 'react';
import authAxios from '../utils/authAxios';
import StandardTableLayout from '../styles/StandardTableLayout';
import {
  Input,
  Select,
  Button,
  ConfirmDelete,
  SecondaryButton,
} from '../styles/TableStyles';
import { mapColumnLabels } from '../styles/ColumnUtils';
import { savedToast, errorToast, deletedToast } from '../utils/toastUtils';

const initialFormState = {
  BagType: '',
  FilmCostMeter: '',
  NetCostMeter: '',
  VexarClipCost: '',
  LabelCost: '',
};

const visibleColumns = mapColumnLabels([
  'BagType',
  'FilmCostMeter',
  'NetCostMeter',
  'VexarClipCost',
  'LabelCost',
]);

const fmt4 = (v) =>
  (v !== null && v !== '' && !isNaN(parseFloat(v)) ? `$${parseFloat(v).toFixed(4)}` : '');

const BagMaterialCostPage = () => {
  const [rows, setRows] = useState([]);
  const [packTypes, setPackTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    fetchData();
    fetchPackTypes();
  }, []);

  const fetchData = () => {
    authAxios.get('/api/bagmaterialcost')
      .then(res => setRows(res.data))
      .catch(err => console.error('Error fetching Bag Material Cost:', err));
  };

  const fetchPackTypes = () => {
    authAxios.get('/api/packtypes')
      .then(res => setPackTypes(res.data))
      .catch(err => console.error('Error fetching Pack Types:', err));
  };

  // Material costs are keyed per group (Type) — one row each.
  const groups = useMemo(
    () => [...new Set(packTypes.map(p => p.Type).filter(Boolean))].sort(),
    [packTypes]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const cleanedForm = {};
      for (let key in formData) {
        cleanedForm[key] = formData[key] === '' ? null : formData[key];
      }
      if (isEditing) {
        await authAxios.put(`/api/bagmaterialcost/${editingId}`, cleanedForm);
      } else {
        await authAxios.post('/api/bagmaterialcost', cleanedForm);
      }
      setFormData(initialFormState);
      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      fetchData();
      savedToast('Bag Material Cost');
    } catch (err) {
      const msg = err.response?.data?.error;
      console.error('Error submitting Bag Material Cost:', msg || err);
      errorToast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (row) => {
    setFormData({
      BagType: row.BagType ?? '',
      FilmCostMeter: row.FilmCostMeter ?? '',
      NetCostMeter: row.NetCostMeter ?? '',
      VexarClipCost: row.VexarClipCost ?? '',
      LabelCost: row.LabelCost ?? '',
    });
    setShowForm(false);
    setIsEditing(true);
    setEditingId(row.UniqueID);
  };

  const handleDelete = async (id) => {
    try {
      await authAxios.delete(`/api/bagmaterialcost/${id}`);
      fetchData();
      deletedToast('Bag Material Cost');
    } catch (err) {
      console.error('Error deleting Bag Material Cost:', err);
    }
  };

  const sortedData = useMemo(() => {
    const sortable = [...rows];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const valA = a[sortConfig.key];
        const valB = b[sortConfig.key];
        if (typeof valA === 'number' && typeof valB === 'number') {
          return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
        }
        return sortConfig.direction === 'asc'
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      });
    }
    return sortable;
  }, [rows, sortConfig]);

  const filteredData = useMemo(() => {
    const lower = searchQuery.toLowerCase();
    return sortedData.filter(row =>
      Object.values(row).some(val =>
        String(val).toLowerCase().includes(lower)
      )
    );
  }, [searchQuery, sortedData]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  return (
    <StandardTableLayout
      title="Bag Material Cost"
      showForm={showForm}
      editingId={editingId}
      submitting={submitting}
      onToggleForm={() => {
        setShowForm(!showForm);
        setIsEditing(false);
        setFormData(initialFormState);
      }}
      onSubmit={handleSubmit}
      columns={visibleColumns}
      formData={formData}
      handleChange={handleChange}
      rows={filteredData}
      searchValue={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      sortConfig={sortConfig}
      onSort={handleSort}
      renderCell={(col, row, handleChange) => {
        if (!handleChange) {
          const text = col === 'BagType' ? String(row?.[col] ?? '') : fmt4(row?.[col]);
          return (
            <span style={{ background: 'transparent', display: 'inline-block', padding: '4px 0' }}>
              {text}
            </span>
          );
        }
        if (col === 'BagType') {
          return (
            <Select name="BagType" aria-label="Group" value={row.BagType || ''} onChange={handleChange}>
              <option value="">Select Group</option>
              {groups.map(g => <option key={g} value={g}>{g}</option>)}
            </Select>
          );
        }
        return (
          <Input
            type="number"
            step="0.0001"
            name={col}
            value={row[col] ?? ''}
            placeholder="$0.0000"
            onChange={handleChange}
          />
        );
      }}
      renderActions={(row) => (
        editingId === row.UniqueID ? (
          <>
            <Button type="submit" disabled={submitting}>Save</Button>
            <SecondaryButton
              type="button"
              onClick={() => {
                setIsEditing(false);
                setEditingId(null);
                setFormData(initialFormState);
              }}
            >
              Cancel
            </SecondaryButton>
          </>
        ) : (
          <>
            <SecondaryButton type="button" onClick={() => handleEdit(row)}>Edit</SecondaryButton>
            <ConfirmDelete onConfirm={() => handleDelete(row.UniqueID)} />
          </>
        )
      )}
    />
  );
};

export default BagMaterialCostPage;
